import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureError } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
    Car, Palette, Hash, Factory, Gauge, Settings, X, Check, Loader2,
    AlertTriangle, Trash2, DollarSign, CircleDot,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ───────────────────────────────────────────────────────────────────

interface VehicleSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vehicleId: number | null;
}

interface VehicleData {
    id: number;
    make: string;
    model: string;
    color: string;
    vin: string;
    manufactured_at: number;
    mileage: number | null;
    engine_size: number;
    description: string | null;
    partner_id: number | null;
    created_at: string;
    updated_at: string;
}

interface Partner {
    id: number;
    name: string;
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEPS = [
    { key: 'details', label: 'Vehicle Details', icon: Car, description: 'Make, model & year', hasRequired: true },
    { key: 'specs', label: 'Specifications', icon: Settings, description: 'Engine, mileage & color', hasRequired: true },
    { key: 'purchase', label: 'Purchase', icon: DollarSign, description: 'Cost & partner', hasRequired: true },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number) {
    return new Intl.NumberFormat('en-GH', {
        style: 'currency', currency: 'GHS',
        minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount);
}

// ─── Field component ─────────────────────────────────────────────────────────

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            {children}
        </div>
    );
}

// ─── Main Sheet ──────────────────────────────────────────────────────────────

export default function VehicleSheet({ open, onOpenChange, vehicleId }: VehicleSheetProps) {
    const queryClient = useQueryClient();
    const isCreate = vehicleId === null;
    const [step, setStep] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Form state
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [vin, setVin] = useState('');
    const [year, setYear] = useState('');
    const [color, setColor] = useState('');
    const [engineSize, setEngineSize] = useState('');
    const [mileage, setMileage] = useState('');
    const [description, setDescription] = useState('');
    const [listingUrl, setListingUrl] = useState('');
    const [partnerId, setPartnerId] = useState('1');
    const [purchasePrice, setPurchasePrice] = useState('');

    // ── Fetch partners ──
    const { data: partners } = useQuery({
        queryKey: ['partners-list'],
        queryFn: async () => {
            const { data, error } = await supabase.from('partners').select('id, name').order('id');
            if (error) throw error;
            return data as Partner[];
        },
        staleTime: 30 * 60 * 1000,
    });

    // ── Fetch vehicle ──
    const { data: vehicle, isLoading } = useQuery({
        queryKey: ['vehicle-sheet', vehicleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicles')
                .select('*, vehicle_procurement(listing_url)')
                .eq('id', vehicleId!)
                .single();
            if (error) throw error;

            // Get purchase transaction via vehicle_transactions
            const { data: vt } = await supabase
                .from('vehicle_transactions')
                .select('transaction_id, transaction_type, transactions:transaction_id(amount)')
                .eq('vehicle_id', vehicleId!)
                .in('transaction_type', ['cif_30', 'landed_cost'])
                .maybeSingle();

            return {
                ...data,
                _procListingUrl: (data.vehicle_procurement as any)?.listing_url || null,
                _purchaseAmount: (vt?.transactions as any)?.amount || 0,
                _purchaseTransactionId: vt?.transaction_id || null,
                _purchaseTransactionType: vt?.transaction_type || null,
            } as VehicleData & {
                _procListingUrl: string | null;
                _purchaseAmount: number;
                _purchaseTransactionId: number | null;
                _purchaseTransactionType: string | null;
            };
        },
        enabled: open && !isCreate,
    });

    // ── Reset form ──
    useEffect(() => {
        if (open) {
            setStep(0);
            setDeleteDialogOpen(false);
            if (isCreate) {
                setMake(''); setModel(''); setVin(''); setYear('');
                setColor(''); setEngineSize(''); setMileage('');
                setDescription(''); setListingUrl('');
                setPartnerId('1'); setPurchasePrice('');
            }
        }
    }, [open, isCreate]);

    // ── Populate form from fetched data ──
    useEffect(() => {
        if (!isCreate && vehicle) {
            setMake(vehicle.make || '');
            setModel(vehicle.model || '');
            setVin(vehicle.vin || '');
            setYear(vehicle.manufactured_at?.toString() || '');
            setColor(vehicle.color || '');
            setEngineSize(vehicle.engine_size?.toString() || '');
            setMileage(vehicle.mileage?.toString() || '');
            setDescription(vehicle.description || '');
            setListingUrl((vehicle as any)?._procListingUrl || '');
            setPartnerId(vehicle.partner_id?.toString() || '1');
            setPurchasePrice((vehicle as any)._purchaseAmount?.toString() || '');
        }
    }, [isCreate, vehicle]);

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['vehicle-sheet'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-analytics'] });
        queryClient.invalidateQueries({ queryKey: ['vehicle-contract-map'] });
    };

    // ── Create ──
    const createMutation = useMutation({
        mutationFn: async () => {
            const price = parseFloat(purchasePrice) || 0;

            // 1. Create purchase transaction
            const { data: tx, error: txErr } = await supabase
                .from('transactions')
                .insert({
                    amount: price,
                    type: 'DEBIT',
                    transaction_at: new Date().toISOString(),
                    description: `Vehicle purchase: ${make.trim()} ${model.trim()}`,
                    currency: 'GHS',
                })
                .select('id')
                .single();
            if (txErr) throw txErr;

            // 2. Create vehicle (without listing_url — goes to vehicle_procurement)
            const { data: v, error: vErr } = await supabase
                .from('vehicles')
                .insert({
                    make: make.trim(),
                    model: model.trim(),
                    vin: vin.trim(),
                    color: color.trim(),
                    manufactured_at: parseInt(year) || 0,
                    engine_size: parseInt(engineSize) || 0,
                    mileage: mileage ? parseInt(mileage) : null,
                    description: description.trim() || null,
                    partner_id: parseInt(partnerId) || 1,
                })
                .select('id')
                .single();
            if (vErr) {
                // Rollback transaction
                await supabase.from('transactions').delete().eq('id', tx.id);
                throw vErr;
            }

            // 2b. Create vehicle_procurement (listing_url)
            if (listingUrl.trim()) {
                await supabase.from('vehicle_procurement').insert({
                    vehicle_id: v.id,
                    listing_url: listingUrl.trim(),
                });
            }

            // 3. Link via vehicle_transactions
            const { error: vtErr } = await supabase
                .from('vehicle_transactions')
                .insert({
                    vehicle_id: v.id,
                    transaction_type: 'landed_cost',
                    transaction_id: tx.id,
                });
            if (vtErr) {
                // Rollback vehicle and transaction
                await supabase.from('vehicles').delete().eq('id', v.id);
                await supabase.from('transactions').delete().eq('id', tx.id);
                throw vtErr;
            }
            return v;
        },
        onSuccess: () => {
            toast.success('Vehicle added to fleet');
            invalidateAll();
            onOpenChange(false);
        },
        onError: (e: any) => {
            captureError(e);
            if (e?.code === '23505' && e?.message?.includes('vin')) toast.error('A vehicle with this VIN already exists');
            else toast.error('Failed to add vehicle');
        },
    });

    // ── Update ──
    const updateMutation = useMutation({
        mutationFn: async () => {
            // Update vehicle (without listing_url — goes to vehicle_procurement)
            const { error: vErr } = await supabase
                .from('vehicles')
                .update({
                    make: make.trim(),
                    model: model.trim(),
                    vin: vin.trim(),
                    color: color.trim(),
                    manufactured_at: parseInt(year) || 0,
                    engine_size: parseInt(engineSize) || 0,
                    mileage: mileage ? parseInt(mileage) : null,
                    description: description.trim() || null,
                    partner_id: parseInt(partnerId) || 1,
                })
                .eq('id', vehicleId!);
            if (vErr) throw vErr;

            // Upsert listing_url to vehicle_procurement
            if (listingUrl.trim()) {
                await supabase.from('vehicle_procurement').upsert(
                    { vehicle_id: vehicleId!, listing_url: listingUrl.trim() },
                    { onConflict: 'vehicle_id' }
                );
            }

            // Update purchase transaction amount via vehicle_transactions
            const txId = (vehicle as any)?._purchaseTransactionId;
            if (txId) {
                const { error: txErr } = await supabase
                    .from('transactions')
                    .update({
                        amount: parseFloat(purchasePrice) || 0,
                        description: `Vehicle purchase: ${make.trim()} ${model.trim()}`,
                    })
                    .eq('id', txId);
                if (txErr) throw txErr;
            }
        },
        onSuccess: () => {
            toast.success('Vehicle updated');
            invalidateAll();
        },
        onError: (e: any) => {
            captureError(e);
            if (e?.code === '23505' && e?.message?.includes('vin')) toast.error('A vehicle with this VIN already exists');
            else toast.error('Failed to update vehicle');
        },
    });

    // ── Delete ──
    const deleteMutation = useMutation({
        mutationFn: async () => {
            // Check for contracts
            const { data: contracts } = await supabase
                .from('contracts')
                .select('id')
                .eq('vehicle_id', vehicleId!)
                .limit(1);
            if (contracts && contracts.length > 0) {
                throw new Error('Cannot delete a vehicle with active contracts. Please remove all contracts first.');
            }

            // Get linked transaction ID from vehicle_transactions
            const { data: vt } = await supabase
                .from('vehicle_transactions')
                .select('transaction_id')
                .eq('vehicle_id', vehicleId!)
                .in('transaction_type', ['cif_30', 'landed_cost'])
                .maybeSingle();
            const txId = vt?.transaction_id;

            // Delete vehicle_transactions first (FK)
            await supabase.from('vehicle_transactions').delete().eq('vehicle_id', vehicleId!);

            const { error } = await supabase.from('vehicles').delete().eq('id', vehicleId!);
            if (error) throw error;

            // Clean up transaction
            if (txId) {
                await supabase.from('transactions').delete().eq('id', txId);
            }
        },
        onSuccess: () => {
            toast.success('Vehicle deleted');
            invalidateAll();
            onOpenChange(false);
        },
        onError: (e: any) => {
            captureError(e);
            toast.error(e?.message || 'Failed to delete vehicle');
        },
    });

    // ── Step validation ──
    const isStepValid = (idx: number) => {
        if (idx === 0) return !!(make.trim() && model.trim() && vin.trim() && year.trim());
        if (idx === 1) return !!(color.trim() && engineSize.trim());
        if (idx === 2) return !!purchasePrice.trim();
        return true;
    };

    const allValid = isStepValid(0) && isStepValid(1) && isStepValid(2);

    const handleSave = () => {
        if (isCreate) createMutation.mutate();
        else updateMutation.mutate();
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    // ── Step content ──
    const renderStep = () => {
        switch (step) {
            case 0: // Vehicle Details
                return (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Make" required>
                                <Input value={make} onChange={e => setMake(e.target.value)} placeholder="e.g. Toyota" />
                            </Field>
                            <Field label="Model" required>
                                <Input value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. Vitz" />
                            </Field>
                        </div>
                        <Field label="VIN / Chassis Number" required>
                            <Input value={vin} onChange={e => setVin(e.target.value)} placeholder="e.g. KSP905224204" />
                        </Field>
                        <Field label="Manufacture Year" required>
                            <Input type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="e.g. 2013" min={1990} max={new Date().getFullYear() + 1} />
                        </Field>
                        <Field label="Description">
                            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional notes about this vehicle" />
                        </Field>
                        <Field label="Listing URL">
                            <Input value={listingUrl} onChange={e => setListingUrl(e.target.value)} placeholder="Link to listing (if applicable)" />
                        </Field>
                    </div>
                );

            case 1: // Specifications
                return (
                    <div className="space-y-5">
                        <Field label="Color" required>
                            <Input value={color} onChange={e => setColor(e.target.value)} placeholder="e.g. Silver, Red, White" />
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Engine Size (cc)" required>
                                <Input type="number" value={engineSize} onChange={e => setEngineSize(e.target.value)} placeholder="e.g. 1300" />
                            </Field>
                            <Field label="Mileage (km)">
                                <Input type="number" value={mileage} onChange={e => setMileage(e.target.value)} placeholder="e.g. 85000" />
                            </Field>
                        </div>
                    </div>
                );

            case 2: // Purchase
                return (
                    <div className="space-y-5">
                        <Field label="Purchase Price (GH₵)" required>
                            <Input
                                type="number"
                                value={purchasePrice}
                                onChange={e => setPurchasePrice(e.target.value)}
                                placeholder="e.g. 45000"
                                step="0.01"
                            />
                        </Field>
                        <Field label="Partner">
                            <Select value={partnerId} onValueChange={setPartnerId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select partner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(partners || []).map(p => (
                                        <SelectItem key={p.id} value={p.id.toString()}>
                                            {p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        {!isCreate && vehicle && (
                            <div className="mt-6 space-y-3">
                                <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                                    System Info
                                </Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-lg border p-3 bg-muted/30">
                                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Vehicle ID</p>
                                        <p className="font-mono text-sm mt-0.5">{vehicle.id}</p>
                                    </div>
                                    <div className="rounded-lg border p-3 bg-muted/30">
                                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Transaction ID</p>
                                        <p className="font-mono text-sm mt-0.5">{(vehicle as any)._purchaseTransactionId || '—'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-lg border p-3 bg-muted/30">
                                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Created</p>
                                        <p className="text-sm mt-0.5">
                                            {new Date(vehicle.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border p-3 bg-muted/30">
                                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Updated</p>
                                        <p className="text-sm mt-0.5">
                                            {new Date(vehicle.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* ── Left Sidebar ── */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-6 relative z-10">
                            {!isCreate && vehicle ? (
                                <>
                                    <h2 className="text-2xl font-semibold tracking-tight mb-3">Manage Vehicle</h2>
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "h-11 w-11 rounded-lg flex items-center justify-center",
                                            "bg-primary/10 text-primary"
                                        )}>
                                            <Car className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-lg font-semibold tracking-tight truncate">
                                                {vehicle.make} {vehicle.model}
                                            </h3>
                                            <p className="text-xs text-muted-foreground">
                                                {vehicle.manufactured_at} · {vehicle.vin}
                                            </p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-semibold tracking-tight">New Vehicle</h2>
                                    <p className="text-sm text-muted-foreground mt-1">Add a new vehicle to the fleet.</p>
                                </>
                            )}
                        </div>

                        {/* Step nav */}
                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isComplete = s.hasRequired && isStepValid(i);

                                return (
                                    <button
                                        key={s.key}
                                        onClick={() => setStep(i)}
                                        className={cn(
                                            "w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                        )}
                                    >
                                        <div className="relative">
                                            <div className={cn(
                                                "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                                isActive ? "bg-primary text-primary-foreground" :
                                                    isComplete ? "bg-green-100 text-green-600/70 dark:bg-green-900/40 dark:text-green-400/70" :
                                                        "bg-muted text-muted-foreground",
                                            )}>
                                                <Icon className={cn("h-5 w-5", isComplete && !isActive && "opacity-60")} />
                                            </div>
                                            {isComplete && !isActive && (
                                                <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                                                    <Check className="h-2.5 w-2.5 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">{s.description}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-auto flex items-center gap-6 relative z-10">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                                onClick={handleClose}
                            >
                                <X className="h-4 w-4 mr-2" />
                                Close
                            </Button>
                            {!isCreate && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-fit text-destructive/70 hover:text-destructive p-0 h-auto hover:bg-transparent"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ── Right Content ── */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">{STEPS[step].label}</h2>
                                <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 flex flex-col overflow-hidden p-6 md:p-10">
                            <div className="flex flex-col h-full max-w-2xl mx-auto w-full">
                                {/* Step title */}
                                <div className="flex-none mb-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                            <p className="text-muted-foreground">{STEPS[step].description}</p>
                                        </div>
                                        {STEPS[step].hasRequired && isStepValid(step) && (
                                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">
                                                <Check className="h-3.5 w-3.5" />
                                                Complete
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Scrollable form */}
                                <ScrollArea className="flex-1 min-h-0">
                                    {isLoading && !isCreate ? (
                                        <div className="space-y-4">
                                            <Skeleton className="h-10 w-full" />
                                            <Skeleton className="h-10 w-full" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                    ) : (
                                        renderStep()
                                    )}
                                </ScrollArea>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleClose}
                                    disabled={isSaving}
                                    className="rounded-full"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    onClick={handleSave}
                                    disabled={!allValid || isSaving}
                                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                >
                                    {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                    {isCreate ? 'Add Vehicle' : 'Save Changes'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Delete Vehicle
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete{' '}
                            <span className="font-medium text-foreground">
                                {vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.vin})` : 'this vehicle'}
                            </span>{' '}
                            and its associated purchase transaction. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => deleteMutation.mutate()}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Delete Vehicle
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
